import { useAxios } from 'hooks/useAxios';
import { useEntitlementsAxios } from 'api/useEntitlementsAxios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { classifyLinkageResponse, type DocumentLinkageResult } from 'shared/documentLinkage';
import { BASE_URL_V2 } from 'shared/constants';
import moment, { type Moment } from 'moment';
import {
  DEAL_TYPE_OPTIONS,
  type CreateNewDealFormValues,
  type SupplierResellerItem
} from 'components/NewDealComponents/types/CreateNewDealFormTypes';
import { buildUnregisteredSupplierId } from 'components/NewDealComponents/SuppliersResellersPicker/unregisteredSupplier';
import type { ConfirmedDocument } from 'components/NewDealComponents/DealDocumentsUpload/types';
import type { components } from 'types/api.generated';
import type {
  GetDealCategoryResponse,
  GetDealContactResponse,
  GetDealCustomFieldResponse,
  GetDealResponse
} from 'components/NewDealComponents/types/ViewDealTypes';
import type { DeliveryDeal } from 'components/charts/DeliveryDealsModel';
import type { Tag } from 'api/useTags';
import type { CustomField } from 'models/CustomField';
import { isNewTagSentinel } from 'components/shared/TagsUtil';
import { formatApiDateOnly, toDealFormTargetDate } from 'components/NewDealComponents/constants/dealListFormat';
import type { PortfolioCompany } from 'models/Portfolio';
import type {
  CustomFieldDef,
  DealScopeFilterMode,
  SubmittedDealRoot,
  SubscriptionPlan
} from 'components/NewDealComponents/SubmittedDealsTable/types';

/** Coalesce concurrent identical GETs (e.g. home bootstrap remounts during Auth0). */
const inflightGetRequests = new Map<string, Promise<unknown>>();

/** Session cache for company-users (survives quick remounts after Auth0). */
let companyUsersSessionCache: CompanyUser[] | undefined;

/**
 * Coalesce concurrent identical GETs (e.g. home bootstrap remounts during Auth0).
 * @template T - Response type for the deduped GET.
 * @param {string} key - Dedupe key.
 * @param {() => Promise<T>} run - Request factory.
 * @returns {Promise<T>} Shared in-flight or new promise.
 */
function dedupeGetRequest<T>(key: string, run: () => Promise<T>): Promise<T> {
  const existing = inflightGetRequests.get(key);
  if (existing) {
    return existing as Promise<T>;
  }
  const promise = run().finally(() => {
    if (inflightGetRequests.get(key) === promise) {
      inflightGetRequests.delete(key);
    }
  });
  inflightGetRequests.set(key, promise);
  return promise;
}

/**
 * Client search result from GET /clients/search (id and name only).
 * @returns {void}
 */
export interface ClientSearchResult {
  id: number;
  name: string;
}

/** Client row from GET /deals/search (SearchClientItemDto). */
export interface DealSearchClientItem {
  id: number;
  name: string;
}

/** Vendor row from GET /deals/search (SearchVendorItemDto). */
export interface DealSearchVendorItem {
  id: number;
  name: string;
}

/** Reseller row from GET /deals/search (SearchResellerItemDto). */
export interface DealSearchResellerItem {
  id: number;
  name: string;
}

/** Deal row from GET /deals/search (SearchDealItemDto). */
export interface DealSearchDealItem {
  dealId: string;
  dealNumber: string;
  subject?: string | null;
  effectiveStatus: string;
}

/** Grouped Elasticsearch-style results from GET /deals/search (SearchResponseDto). */
export interface DealSearchResponse {
  query: string;
  clients: DealSearchClientItem[];
  vendors: DealSearchVendorItem[];
  resellers: DealSearchResellerItem[];
  deals: DealSearchDealItem[];
}

/** Entity buckets for GET /deals/search `types` (API `SearchEntityType`). */
export type DealSearchEntityType = 'client' | 'vendor' | 'reseller' | 'deal';

/**
 * Primary delivery contact on a client contract (nested on {@link ClientActiveContract}).
 * Aligns with API `ClientPrimaryDeliveryContactDto` from GET /clients/{clientId}/contracts.
 */
export interface ClientPrimaryDeliveryContact {
  id: string;
  name: string;
  email: string;
}

/**
 * One entitled service (report type) for a client contract, from
 * GET /clients/{clientId}/contracts/{contractId}/entitlements (`ContractEntitlementDto`).
 * Drives the benchmark-request "Report Type Requested" picker. For `Unlimited`,
 * `totalQuantity` and `remaining` are null; only `Delivered` requests draw down `remaining`.
 */
export interface ContractEntitlement {
  serviceTypeCode: string;
  /** Mapped `lov_report_type.id` so the request keeps storing `deal.report_type_id`; null when unmapped. */
  reportTypeId: number | null;
  label: string;
  description: string | null;
  entitlementType: 'Limited' | 'Unlimited';
  totalQuantity: number | null;
  delivered: number;
  inProgress: number;
  remaining: number | null;
}

/**
 * Response of GET /clients/{clientId}/portfolio-entitlements (`PortfolioEntitlementsDto`, VE-2236).
 * For a portfolio account the entitlements come from the top-level (root) contract; `isPortfolio` is
 * false for standalone clients, in which case `entitlements` is empty. Drives the TCV tier picker.
 */
export interface PortfolioEntitlements {
  isPortfolio: boolean;
  rootClientId: number | null;
  /** The root's current `client_contract.id`, for persisting an intake request against the root contract. */
  rootContractId: number | null;
  entitlements: ContractEntitlement[];
}

/**
 * A Total Contract Value tier (GET /deals/tcv-tiers, backed by `lov_tcv_tier`). `id` is the persisted
 * `deal.tcv_tier_id`; `code` is the stable identifier. Single source of truth for the tier UI + persistence.
 */
export interface TcvTier {
  id: number;
  code: string;
  label: string;
  minValue: number;
  maxValue: number | null;
  mode: 'fixed' | 'entitlements';
  reportTypeLabel: string | null;
  deliverable: string | null;
  slaText: string | null;
}

/**
 * Active client contract from GET /clients/{clientId}/contracts (`ClientActiveContractDto`).
 * `contractId` is internal `client_contract.id` (maps to `client_contract_id` on POST /deals).
 */
export interface ClientActiveContract {
  contractId: number;
  clientId: number;
  name: string;
  startDate: string;
  endDate: string;
  contractNumber: string;
  status: string;
  contractTypeLabel?: string | null;
  entitlementsCap?: number | null;
  dealCount: number;
  remainingEntitlements?: number | null;
  /** True when this row matches `client.current_contract_id` (see GET /clients/:id/contracts). */
  currentContract?: boolean;
  /**
   * True for NPI-owned placeholder rows (orphan + yearly POC) the API prepends to every client's
   * list. Real client contracts are false/undefined. Used to avoid defaulting to a placeholder.
   */
  isPlaceholder?: boolean;
  primaryDeliveryContact?: ClientPrimaryDeliveryContact | null;
}

/** Document category appliesTo enum values from GET /lov/document-category. */
export type LovDocumentCategoryAppliesTo = 'DEAL' | 'PROPOSAL' | 'BOTH';

/** Document category list item from GET /lov/document-category. */
export interface LovDocumentCategory {
  id: number;
  code: string;
  name: string;
  description?: string | null;
  appliesTo: LovDocumentCategoryAppliesTo;
  isActive: boolean;
}

/** Report type list item from GET /lov/report-type. */
export interface LovReportType {
  id: number;
  label: string;
  sfPicklistValue: string;
  description?: string | null;
}

/** Currency row from GET /lov/currency. */
export interface LovCurrency {
  currency_code: string;
}

/** Row from GET /supplier/:id/deals (`SupplierDealDto`). */
export interface SupplierDeal {
  Id: string;
  Name: string;
  CreatedDate: string | Date;
  Feedback_Date__c?: string | Date | null;
  Start_Date__c?: string | Date | null;
  Subject__c?: string | null;
  Account_Manager__r?: { Id?: string | null; Name?: string | null } | null;
  NPI_Analyst__r?: { Id?: string | null; Name?: string | null } | null;
  Client__r?: { Id?: string | null; Name?: string | null } | null;
  Savings_Achieved__c?: string | null;
  Savings_Achieved_Year_1__c?: number | null;
  Savings_Achieved_Full_Contract_Term__c?: number | null;
  Term__c?: string | null;
  Deal_Status__c?: string | null;
  Status__c?: string | null;
  Contract_Type__c?: string | null;
  Contract_Start_Date__c?: string | Date | null;
  Contract_Renewal_Date__c?: string | Date | null;
  Total_Savings_1yr__c?: number | null;
  Total_Savings_3yr__c?: number | null;
  Deal_Reason__c?: string | null;
  Initial_Quote_Full_Contract_Term__c?: number | null;
  NPI_Savings_Target__c?: number | null;
  NPI_Savings_Target_Full_Contract_Term__c?: number | null;
  Final_Amount_Full_Contract_Term__c?: number | null;
  FMV_Report_Link__c?: string | null;
  contract_id?: number;
}

/** Wrapper from GET /supplier/:id/deals (`SupplierDealsResponseDto`). */
export interface SupplierDealsResponse {
  status: 'success';
  data: SupplierDeal[];
}

/** Query for GET /api/deals (`ClientGetDealsQueryDto` on Nest `DealClientController`). */
export interface GetClientDealsQuery {
  includeAll?: boolean;
  lastModifiedDate?: string;
}

/** Nested deal summary on GET /api/deals rows (`DealDto`). */
export interface ClientDealListingDeal {
  startDate?: Date | string | null;
  renewalDate?: Date | string | null;
  term?: string | null;
  noticePeriod?: string | null;
  dealType?: string;
  dealStatus?: string;
  description?: string | null;
}

/** Id + name slice used on listing rows (supplier, org, contacts, etc.). */
export interface ClientDealListingOwner {
  id?: string;
  name?: string;
}

/** NPI account owner email/name on listing rows. */
export interface ClientDealListingAccountOwner {
  name?: string;
  email?: string;
}

/** Related NPI contract on GET /api/deals rows (`NpiContractDto`). */
export interface ClientDealListingNpiContract {
  id?: string;
  name?: string;
  startDate?: Date | string;
  endDate?: Date | string;
  contractType?: string;
}

/**
 * One row from GET /api/deals (Nest `DealListingRecordDto` after class-transformer).
 * Dates may arrive as ISO strings from JSON.
 */
export interface ClientDealListingRecord {
  id?: string;
  subject?: string;
  deal?: ClientDealListingDeal;
  supplier?: ClientDealListingOwner;
  organization?: ClientDealListingOwner;
  clientDealOwner?: ClientDealListingOwner;
  createdDate?: Date | string;
  findingsProvidedDate?: Date | string | null;
  startDate?: Date | string;
  npiAccountOwner?: ClientDealListingAccountOwner;
  npiAccountManager?: ClientDealListingOwner;
  npiContract?: ClientDealListingNpiContract;
  savingsAchieved?: string | null;
  savingsAchievedYear1?: number;
  savingsAchievedFullContractTerm?: number;
  lastModifiedDate?: Date | string;
  totalSavings1yr?: number;
  totalSavings3yr?: number;
  initialQuoteFirstYear?: number;
  initialQuoteFullContractTerm?: number;
  npiSavingsTargetFirstYear?: number;
  npiSavingsTargetFullContractTerm?: number;
  finalAmountFullContractTerm?: number;
  fmvReportLink?: string | null;
  customFields?: Record<string, unknown>;
}

/** Query params for GET /deals/open (`GetOpenDealsQueryDto`). */
export interface GetOpenDealsQuery {
  vendorId?: number;
  search?: string;
  includeProposals?: boolean;
}

export interface OpenDealVendor {
  id: number;
  name?: string | null;
  /**
   * Primary deal-party id for the supplier-deals drilldown. Optional: the open/scoped list API does not
   * populate it today (`OpenDealVendorDto` returns only id/name), so consumers must treat it as possibly
   * absent — end-to-end wiring (API DTO + mapper) is a separate follow-up.
   */
  primaryDealPartyId?: string | null;
}

export interface OpenDealReseller {
  id: number;
  name?: string | null;
}

export interface OpenDealClient {
  id: number;
  name?: string | null;
  salesforceId?: string | null;
}

export interface OpenDealContactPerson {
  userId: string;
  name?: string | null;
  color?: string | null;
}

export interface OpenDealProposalSummary {
  id: string;
  subject: string;
  status: string;
  startDate?: string | null;
  proposalDate: string;
  expirationDate?: string | null;
  proposalTotalValue?: number | null;
  contractId?: number | null;
  isFinalAgreed: boolean;
  isActualContract: boolean;
  isAnalysisTarget: boolean;
  lineItemCount: number;
}

export interface OpenDealItem {
  dealId: string;
  dealNumber: string;
  salesforceId?: string | null;
  subject?: string | null;
  effectiveStatus: string;
  dealReason?: string | null;
  reportType?: string | null;
  /** Friendly service name for entitlement-driven report types (whose `reportType` enum is null). */
  reportTypeLabel?: string | null;
  targetPurchaseDate?: string | null;
  client?: OpenDealClient | null;
  vendor?: OpenDealVendor | null;
  reseller?: OpenDealReseller | null;
  dataUseRights?: string | null;
  startDate?: string | null;
  renewalDate?: string | null;
  daysOpen?: number | null;
  lineItemCount: number;
  primaryAnalyst?: OpenDealContactPerson | null;
  csd?: OpenDealContactPerson | null;
  clientContact?: OpenDealContactPerson | null;
  commentsToCustomer?: string | null;
  createdAt: string;
  updatedAt: string;
  currencyCode?: string | null;
  proposals: OpenDealProposalSummary[];
}

export interface OpenDealsAverages {
  /** Legacy/display-ready value (e.g. `"101 Days"`). */
  avgOpenDeals?: string | null;
  /** Legacy/display-ready value (e.g. `"1200 Hours"`). */
  avgResponseTime?: string | null;
  /** New API shape: numeric/open value + optional unit. */
  avgOpenDealBusinessDays?: number | string | null;
  avgResponseTimeBusinessDays?: number | string | null;
  avgOpenDealsUnit?: string | null;
  avgResponseTimeUnit?: string | null;
  avgOpenDealBusinessDaysUnit?: string | null;
  avgResponseTimeBusinessDaysUnit?: string | null;
}

export interface OpenDealsResponse {
  items: OpenDealItem[];
  total: number;
  averages: OpenDealsAverages;
}

export interface CompanyUser {
  id: string;
  fullName?: string | null;
  email?: string | null;
  title?: string | null;
  color?: string | null;
  blocked: boolean;
}

export interface UpdateDealAnalystResponse {
  dealId: string;
  analystName?: string | null;
  analystColor?: string | null;
  analystUserId?: string | null;
  /** Present when status was promoted (e.g. Pending Assignment → In Progress). */
  effectiveStatus?: string | null;
  updatedAt: string;
}

export interface UpdateDealCommentsResponse {
  dealId: string;
  commentsToCustomer?: string | null;
  updatedAt: string;
}

/** Response body from DELETE /deals/:id (`DeleteDealResponseDto`). */
export interface DeleteDealResponse {
  dealId: string;
  message: string;
}

/** Response body from POST /deals (201) and PATCH /deals/:id. */
export interface CreateDealResponse {
  dealId: string;
  deal_number: string;
  subject?: string;
  /** Persisted ISO 4217 code when returned by API. */
  currency_code?: string;
}

export interface DealUpdateRelationBaseline {
  categories?: GetDealCategoryResponse[];
  tagIds?: CreateNewDealFormValues['tagIds'];
  /**
   * The loaded deal's supplier rows, used to tell a real supplier change from a scalar-only edit
   * (VE-2570). Without it a removal or a primary-flag change is invisible, because every remaining
   * row still carries the `partyId` it was hydrated with.
   */
  suppliers?: { vendor_id: number | null; is_primary: boolean }[];
}

/**
 * Order-independent fingerprint of a supplier set.
 *
 * Pending (id-less) rows cannot be expressed through `vendors` — the API rejects `vendor_name` on
 * update — but their PRESENCE still has to register, because removing one is a real change to the
 * deal. Counting them was the missing half: excluding them entirely made "delete the pending
 * supplier" fingerprint-identical to "change nothing", so the replace was never sent and the removal
 * vanished on save (VE-2656).
 * @param {{ vendor_id: number | null; is_primary: boolean }[]} rows - Supplier rows to fingerprint
 * @returns {string} Stable key that changes when a supplier, its primary flag, or the pending count changes
 */
function supplierSetKey(rows: { vendor_id: number | null; is_primary: boolean }[]): string {
  const registered = rows
    .filter((r) => r.vendor_id != null)
    .map((r) => `${r.vendor_id}:${r.is_primary ? 1 : 0}`)
    .sort()
    .join('|');
  const pendingCount = rows.filter((r) => r.vendor_id == null).length;
  return `${registered}#pending:${pendingCount}`;
}

type DealCategoryApiRow = {
  category_root_id: number;
  category_prime_id: number;
  category_second_id?: number;
};

/** Contact row from GET /contacts/from-db (users table; Client vs NPI). */
export interface DbContactFromDb {
  id: string;
  type: 'Client' | 'NPI';
  name: string;
  email: string;
  auth0UserId?: string;
  salesforceContactId?: string | null;
  salesforceUserId: string | null;
  designatedType?: string | null;
  label?: string | null;
}

/** Response when GET /contacts/from-db?groupBy=type */
export interface DbContactsGroupedFromDb {
  npi: DbContactFromDb[];
  client: DbContactFromDb[];
}

/** `contactType` query for GET /contacts/from-db (ContactController.searchContactsFromDb). */
export type ContactsFromDbContactTypeParam = 'NPI' | 'Client' | 'all';

/** Query params for GET /contacts/from-db (DB search / list; no Salesforce). */
export interface GetContactsFromDbParams {
  /** Optional profile switch client id (same as other Nest /contacts calls). */
  profileId?: number;
  /** Pass `'type'` to receive {@link DbContactsGroupedFromDb}; omit for a flat list. */
  groupBy?: 'type';
  /**
   * Case-insensitive substring on display name (server-side).
   * Ignored when contactType is `all`.
   */
  name?: string;
  /** Limit to NPI users, client contacts for scoped client, or all in scope. */
  contactType?: ContactsFromDbContactTypeParam;
  /**
   * When true, include DB-only client contacts that have no Auth0 account yet (null `auth0_user_id`).
   * Used by the deal-details contact picker so newly-provisioned (unregistered) contacts are findable.
   */
  includeUnregistered?: boolean;
}

/**
 * Maps `contact_type` from GET /deals/:id to NPI vs client bucket and display label.
 * Keep in sync with `CONTACT_TYPE_OPTIONS` in ContactsSection.tsx.
 */
const GET_DEAL_CONTACT_TYPE_META: Record<string, { type: 'client' | 'npi'; visualLabel: string }> = {
  Primary_Client_Contact: { type: 'client', visualLabel: 'Primary Contact' },
  Secondary_Client_Contact: { type: 'client', visualLabel: 'Secondary Contact' },
  Primary_Analyst: { type: 'npi', visualLabel: 'Primary Analyst' },
  Secondary_Analyst: { type: 'npi', visualLabel: 'Secondary Analyst' },
  Deal_Desk: { type: 'npi', visualLabel: 'DealDesk' },
  Client_Services_Director: { type: 'npi', visualLabel: 'Client Services Director' },
  Deal_Owner: { type: 'client', visualLabel: 'Deal Owner' }
};

/**
 * Contact roles a client (own-company) caller is allowed to send on POST/PATCH /deals.
 * Mirrors `CLIENT_SETTABLE_CONTACT_TYPES` in the API's deal.controller.ts — keep the two in sync.
 */
export const CLIENT_SETTABLE_CONTACT_TYPES: readonly string[] = [
  'Primary_Client_Contact',
  'Secondary_Client_Contact',
  'Client_Services_Director'
];

/**
 * Drops the contact roles a client caller may not set (Primary/Secondary Analyst, Deal Desk).
 *
 * The client edit screen hydrates every contact GET /deals/:id returns, hides the NPI bucket, and
 * would otherwise echo those unseen rows back on save — which the API refuses, making the deal
 * unsavable by its own client once an analyst was assigned (VE-2626). The API also drops them, so
 * this is the near-side half of the same rule: don't send what the caller cannot set.
 * @param {DbContactsGroupedFromDb | undefined} grouped - Form `contacts` value.
 * @returns {DbContactsGroupedFromDb} Grouped contacts carrying only client-settable roles.
 */
export function filterClientSettableContacts(
  grouped: DbContactsGroupedFromDb | null | undefined
): DbContactsGroupedFromDb {
  const settable = (list: DbContactFromDb[] | null | undefined): DbContactFromDb[] =>
    (list ?? []).filter((c) => c != null && CLIENT_SETTABLE_CONTACT_TYPES.includes(c.designatedType ?? ''));
  return { npi: settable(grouped?.npi), client: settable(grouped?.client) };
}

/**
 * @param {GetDealResponse} deal - GET /deals/:id body
 * @returns {number | undefined} Internal client id for the form client field
 */
export function parseDealClientId(deal: GetDealResponse): number | undefined {
  const raw = deal.client_id ?? deal.client?.id;
  if (raw === undefined || raw === null) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * @param {string | null | undefined} dealReason - `deal_reason` from API
 * @returns {string | undefined} Form `dealType` when it matches a known option
 */
export function mapDealReasonToFormDealType(dealReason?: string | null): string | undefined {
  if (dealReason == null || dealReason === '') return undefined;
  return DEAL_TYPE_OPTIONS.some((o) => o.value === dealReason) ? dealReason : undefined;
}

/**
 * Maps a loaded deal's suppliers back into form rows. A supplier awaiting provisioning has no
 * `vendor_id` (only the free-text name it was submitted with), so it is restored as an unregistered
 * row — that keeps the "Provisioning pending" treatment visible on edit/reload instead of the row
 * silently degrading into `Vendor null`.
 * @param {GetDealResponse['vendors']} vendors - Deal vendors from GET /deals/:id
 * @returns {SupplierResellerItem[]} Supplier rows for the form
 */
export function mapDealVendorsToSuppliers(vendors: GetDealResponse['vendors']): SupplierResellerItem[] {
  return (vendors ?? []).map((v) => {
    const name = (v.vendor_name ?? '').trim();
    if (v.vendor_id == null) {
      // The name can be absent for a pending supplier (it is only persisted once
      // `deal_party.vendor_name` exists), so fall back to a label rather than an empty chip. `party_id`
      // keys the row so two unnamed pending suppliers stay distinct.
      return {
        id: name ? buildUnregisteredSupplierId(name) : `unregistered:${v.party_id}`,
        name: name || 'Pending supplier',
        isPrimary: v.is_primary,
        isUnregistered: true,
        // Carried so an analyst resolving this row can name the exact party in `vendor_links`.
        partyId: v.party_id,
        wasPending: true
      };
    }
    // `partyId` marks the row as hydrated (not newly added), so the submit mapping never mistakes an
    // existing supplier for one to create. `wasPending` is absent here, which is what keeps this row
    // out of `vendor_links` — re-linking an already-linked party is rejected by the API.
    return {
      id: v.vendor_id,
      name: name || `Vendor ${v.vendor_id}`,
      isPrimary: v.is_primary,
      partyId: v.party_id
    };
  });
}

/**
 * @param {GetDealResponse['resellers']} resellers - Deal resellers from GET /deals/:id
 * @returns {SupplierResellerItem[] | undefined} Reseller rows, or undefined if none
 */
export function mapDealResellersToFormResellers(
  resellers: GetDealResponse['resellers']
): SupplierResellerItem[] | undefined {
  const list = resellers ?? [];
  if (list.length === 0) return undefined;
  return list.map((r) => {
    const name = (r.reseller_name ?? '').trim();
    if (r.reseller_id == null) {
      // Awaiting provisioning: keep the pending treatment visible on reload rather than degrading to
      // "Reseller null". `partyId` marks the row as hydrated; `wasPending` survives a later resolve.
      return {
        id: name ? buildUnregisteredSupplierId(name) : `unregistered:${r.party_id}`,
        name: name || 'Pending reseller',
        isPrimary: r.is_lead_reseller,
        isUnregistered: true,
        partyId: r.party_id,
        wasPending: true
      };
    }
    return {
      id: r.reseller_id,
      name: name || `Reseller ${r.reseller_id}`,
      isPrimary: r.is_lead_reseller,
      partyId: r.party_id
    };
  });
}

/**
 * Maps deal-assigned contacts into the grouped shape used by Create New Deal / ContactsSection.
 * Rows with unknown `contact_type` are skipped.
 * @param {GetDealContactResponse[] | undefined} contacts - `deal.contacts`
 * @returns {DbContactsGroupedFromDb} Grouped NPI vs client contacts for the form
 */
export function mapGetDealContactsToFormContacts(
  contacts: GetDealContactResponse[] | undefined
): DbContactsGroupedFromDb {
  const result: DbContactsGroupedFromDb = { npi: [], client: [] };
  for (const c of contacts ?? []) {
    const meta = GET_DEAL_CONTACT_TYPE_META[c.contact_type];
    if (!meta) continue;
    const userId = (c.user_id ?? '').trim();
    if (!userId) continue;
    const first = (c.first_name ?? '').trim();
    const last = (c.last_name ?? '').trim();
    const name = [first, last].filter(Boolean).join(' ') || 'Unknown';
    const row: DbContactFromDb = {
      // `c.id` is deal_contact row id; POST/PATCH expect `users.id` as user_id (see buildNewDealCreateFormData).
      id: userId,
      type: meta.type === 'client' ? 'Client' : 'NPI',
      name,
      email: c.email?.trim() || 'N/A',
      auth0UserId: undefined,
      salesforceContactId: null,
      salesforceUserId: null,
      designatedType: c.contact_type,
      label: meta.visualLabel
    };
    if (meta.type === 'client') result.client.push(row);
    else result.npi.push(row);
  }
  return result;
}

/**
 * Maps GET /deals/:id category buckets into the form's `category` shape (matches `CategoryManager`).
 * Returns `undefined` when no categories are attached so the form field stays untouched.
 * @param {GetDealCategoryResponse[] | undefined} categories - `deal.categories`
 * @returns {CreateNewDealFormValues['category']} Category rows for the form, or undefined
 */
export function mapGetDealCategoriesToFormCategories(
  categories: GetDealCategoryResponse[] | undefined
): CreateNewDealFormValues['category'] {
  const list = categories ?? [];
  if (list.length === 0) return undefined;
  return list.map((bucket) => ({
    mainCategory: (bucket.category_root_name ?? '').trim(),
    subCategory: (bucket.category_prime_name ?? '').trim(),
    productCategories: bucket.category_second_names ?? [],
    categoryRootId: bucket.category_root_id,
    categoryPrimeId: bucket.category_prime_id,
    categorySecondIds: bucket.category_second_ids ?? []
  }));
}

/**
 * Flattens the form's grouped `category` value into the API payload shape (one row per `categorySecondIds`
 * entry; one row with `category_second_id` omitted when no seconds are selected).
 * @param {CreateNewDealFormValues['category']} categories - `values.category`
 * @returns {Array<{ category_root_id: number; category_prime_id: number; category_second_id?: number }>} Rows for `deal_category`
 */
export function flattenFormCategoriesForApi(categories: CreateNewDealFormValues['category']): DealCategoryApiRow[] {
  const out: DealCategoryApiRow[] = [];
  for (const bucket of categories ?? []) {
    const rootId = Number(bucket.categoryRootId);
    const primeId = Number(bucket.categoryPrimeId);
    if (!Number.isInteger(rootId) || rootId < 1 || !Number.isInteger(primeId) || primeId < 1) {
      continue;
    }
    const seconds = (bucket.categorySecondIds ?? [])
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id >= 1);
    if (seconds.length === 0) {
      out.push({ category_root_id: rootId, category_prime_id: primeId });
    } else {
      for (const secondId of seconds) {
        out.push({ category_root_id: rootId, category_prime_id: primeId, category_second_id: secondId });
      }
    }
  }
  return out;
}

/**
 *
 * @param categories
 */
function flattenGetDealCategoriesForApi(categories: GetDealCategoryResponse[] | undefined): DealCategoryApiRow[] {
  const out: DealCategoryApiRow[] = [];
  for (const bucket of categories ?? []) {
    const rootId = Number(bucket.category_root_id);
    const primeId = Number(bucket.category_prime_id);
    if (!Number.isInteger(rootId) || rootId < 1 || !Number.isInteger(primeId) || primeId < 1) continue;
    const seconds = (bucket.category_second_ids ?? [])
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id >= 1);
    if (seconds.length === 0) {
      out.push({ category_root_id: rootId, category_prime_id: primeId });
    } else {
      for (const secondId of seconds) {
        out.push({ category_root_id: rootId, category_prime_id: primeId, category_second_id: secondId });
      }
    }
  }
  return out;
}

/**
 *
 * @param rows
 */
function normalizeCategoryRowsForCompare(rows: DealCategoryApiRow[]): string[] {
  return rows.map((row) => `${row.category_root_id}:${row.category_prime_id}:${row.category_second_id ?? ''}`).sort();
}

/**
 *
 * @param a
 * @param b
 */
function areCategoryRowsEqual(a: DealCategoryApiRow[], b: DealCategoryApiRow[]): boolean {
  const left = normalizeCategoryRowsForCompare(a);
  const right = normalizeCategoryRowsForCompare(b);
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

/**
 *
 * @param currentRows
 * @param baselineRows
 */
function mergePartialCategoryRowsForPatch(
  currentRows: DealCategoryApiRow[],
  baselineRows?: DealCategoryApiRow[]
): DealCategoryApiRow[] {
  if (currentRows.length === 0 || !baselineRows || baselineRows.length === 0) return currentRows;
  const currentKeys = new Set(normalizeCategoryRowsForCompare(currentRows));
  const hasBaselineOverlap = normalizeCategoryRowsForCompare(baselineRows).some((key) => currentKeys.has(key));
  if (hasBaselineOverlap) return currentRows;

  const byKey = new Map<string, DealCategoryApiRow>();
  for (const row of [...baselineRows, ...currentRows]) {
    const key = `${row.category_root_id}:${row.category_prime_id}:${row.category_second_id ?? ''}`;
    if (!byKey.has(key)) byKey.set(key, row);
  }
  return Array.from(byKey.values());
}

/**
 *
 * @param currentTagIds
 * @param baselineTagIds
 */
function mergePartialTagIdsForPatch(
  currentTagIds: CreateNewDealFormValues['tagIds'],
  baselineTagIds?: number[]
): CreateNewDealFormValues['tagIds'] {
  if (!currentTagIds || currentTagIds.length === 0 || !baselineTagIds || baselineTagIds.length === 0)
    return currentTagIds;
  const currentNumericIds = normalizeFormTagIdsForApi(currentTagIds);
  const hasBaselineOverlap = baselineTagIds.some((id) => currentNumericIds.includes(id));
  if (hasBaselineOverlap) return currentTagIds;

  return [...baselineTagIds, ...currentTagIds];
}

/**
 *
 * @param a
 * @param b
 */
function areNumberArraysEqual(a: number[], b: number[]): boolean {
  const left = [...a].sort((x, y) => x - y);
  const right = [...b].sort((x, y) => x - y);
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

/**
 *
 * @param tagIds
 */
function hasNewTagSentinel(tagIds: CreateNewDealFormValues['tagIds']): boolean {
  return (tagIds ?? []).some((tagId) => isNewTagSentinel(tagId));
}

/**
 * Coerces the form's `tagIds` (mixed number/string from TagsSelect) into the numeric `tag_ids` payload.
 * `newTag:<n>` sentinel strings (see {@link isNewTagSentinel}) and non-numeric values are dropped.
 * @param {CreateNewDealFormValues['tagIds']} tagIds - `values.tagIds`
 * @returns {number[]} Deduplicated tag ids
 */
export function normalizeFormTagIdsForApi(tagIds: CreateNewDealFormValues['tagIds']): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  for (const raw of tagIds ?? []) {
    if (isNewTagSentinel(raw)) continue;
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1 || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

/**
 * Appends `tag_ids` and `new_tags` to deal multipart FormData (POST/PATCH /deals).
 * Custom tags use {@link isNewTagSentinel}-matching string ids in the form; resolved via the `tags`
 * list from TagsSelect state to produce a `{ name, color }` entry for the API.
 * @param {FormData} formData - Multipart body to mutate
 * @param {CreateNewDealFormValues['tagIds']} tagIds - Form `tagIds` field
 * @param {Tag[]} tags - Tag options including in-form created entries
 * @param {{ sendEmptyTagIds?: boolean }} [options] - Optional append behavior.
 * @param {boolean} [options.sendEmptyTagIds] - When true, append empty `tag_ids` to clear tags on PATCH.
 * @returns {void}
 */
export function appendDealTagsToFormData(
  formData: FormData,
  tagIds: CreateNewDealFormValues['tagIds'] | undefined,
  tags: Tag[],
  options?: { sendEmptyTagIds?: boolean }
): void {
  if (tagIds === undefined) return;

  const newTagSentinels: string[] = [];
  const existingTagIds: Array<number | string> = [];
  for (const raw of tagIds) {
    if (isNewTagSentinel(raw)) {
      newTagSentinels.push(raw);
    } else {
      existingTagIds.push(raw);
    }
  }
  const normalizedExisting = normalizeFormTagIdsForApi(existingTagIds);

  if (normalizedExisting.length > 0 || options?.sendEmptyTagIds) {
    formData.append('tag_ids', JSON.stringify(normalizedExisting));
  }

  if (newTagSentinels.length > 0) {
    const newTags = newTagSentinels
      .map((tagId) => {
        const found = tags.find((tag) => tag.id === tagId);
        if (!found) return undefined;
        return { name: found.name, color: found.color };
      })
      .filter((tag): tag is { name: string; color: string } => Boolean(tag));

    if (newTags.length > 0) {
      formData.append('new_tags', JSON.stringify(newTags));
    }
  }
}

/**
 * Collects client custom field values from the form into the `custom_fields` payload for POST/PATCH /deals.
 * Custom field inputs are dynamic keys on the form (one per `custom_field.key`, rendered by `renderCustomFields`),
 * so they are read from the raw values bag, not the typed `CreateNewDealFormValues`. Only enabled fields with a
 * non-empty value are included. Raw values (e.g. dayjs dates, booleans, numbers) are preserved and serialized by
 * the builder's `JSON.stringify`; the API coerces each to a string for `custom_field_value.val`.
 * @param {CreateNewDealFormValues} values - Ant Design form values (includes dynamic custom field keys).
 * @param {CustomField[]} customFields - Enabled/published custom field definitions for the client.
 * @returns {Array<{ custom_field_id: number; value: unknown }>} Rows for the `custom_fields` field.
 */
export function collectDealCustomFieldsForApi(
  values: CreateNewDealFormValues,
  customFields: CustomField[]
): Array<{ custom_field_id: number; value: unknown }> {
  const bag = values as unknown as Record<string, unknown>;
  const out: Array<{ custom_field_id: number; value: unknown }> = [];
  for (const field of customFields ?? []) {
    if (!field.isEnabled || field.id == null) continue;
    const raw = bag[field.key];
    if (raw === undefined || raw === null || raw === '') continue;
    // Guard against invalid Moment/Dayjs objects that JSON-serialize to null
    if (typeof raw === 'object' && JSON.stringify(raw) === 'null') continue;
    out.push({ custom_field_id: field.id, value: raw });
  }
  return out;
}

/**
 * Converts a GET /deals/:id custom field value (always a string) into the form value shape expected by
 * `renderCustomFields` for its `data_type`. Dates become **moment** objects (antd v4 DatePicker), checkboxes
 * become booleans, numbers become numbers.
 * @param {GetDealCustomFieldResponse} cf - Custom field value row from the deal response.
 * @returns {unknown} Form value, or undefined when empty.
 */
function customFieldResponseValueToFormValue(cf: GetDealCustomFieldResponse): unknown {
  const v = cf.value;
  if (v === undefined || v === null || v === '') return undefined;
  switch (cf.data_type) {
    case 'number': {
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    }
    case 'boolean':
      return v === 'true' || v === '1';
    case 'date': {
      const m = moment(v);
      return m.isValid() ? m : undefined;
    }
    default:
      return v;
  }
}

/**
 * Maps GET /deals/:id `custom_fields` into a form-values record keyed by each field's `key`
 * (so `setFieldsValue` rehydrates the dynamic Form.Items rendered by `renderCustomFields`).
 * @param {GetDealCustomFieldResponse[] | undefined} customFields - `deal.custom_fields`
 * @returns {Record<string, unknown>} Form values keyed by custom field key.
 */
export function mapDealCustomFieldsToFormValues(
  customFields: GetDealCustomFieldResponse[] | undefined
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const cf of customFields ?? []) {
    out[cf.key] = customFieldResponseValueToFormValue(cf);
  }
  return out;
}

/**
 * Friendly report-type label for deal detail surfaces. Prefer the service name bridged from
 * `lov_service_type` over `report_type_label`, which may hold a raw code (e.g. `SVC_005`).
 * @param {Pick<GetDealResponse, 'report_type_service_label' | 'report_type_label'> | null | undefined} deal - Loaded deal
 * @returns {string | null | undefined} Label to render
 */
export function resolveDealReportTypeDisplayLabel(
  deal: Pick<GetDealResponse, 'report_type_service_label' | 'report_type_label'> | null | undefined
): string | null | undefined {
  return deal?.report_type_service_label ?? deal?.report_type_label ?? undefined;
}

/**
 * Label for a report-type Select option when the deal's current type is outside the default set.
 * Prefers the friendly service name, then the catalog label, then the stored report-type label, then id.
 * @param {Pick<GetDealResponse, 'report_type_service_label' | 'report_type_label'> | null | undefined} deal - Loaded deal
 * @param {string | undefined} matchLabel - Label from the report-types catalog when ids align
 * @param {number} reportTypeId - Current deal report type id
 * @returns {string} Option label
 */
export function resolveReportTypePickerLabel(
  deal: Pick<GetDealResponse, 'report_type_service_label' | 'report_type_label'> | null | undefined,
  matchLabel: string | undefined,
  reportTypeId: number
): string {
  return deal?.report_type_service_label ?? matchLabel ?? deal?.report_type_label ?? String(reportTypeId);
}

/**
 * Reads the persisted `deal.tcv_tier_id` (VE-2236). The optional field is intersected onto
 * `GetDealResponse` so this works before the generated type picks the column up (the value is present
 * at runtime once the API returns it).
 * @param {GetDealResponse & { tcv_tier_id?: number | null }} deal - The loaded deal.
 * @returns {number | undefined} The persisted tier id, or undefined.
 */
export function readDealTcvTierId(deal: GetDealResponse & { tcv_tier_id?: number | null }): number | undefined {
  return deal.tcv_tier_id ?? undefined;
}

/**
 * Maps GET /deals/:id payload into Create New Deal form field shapes (edit / prefill).
 * @param {GetDealResponse} deal - Response from useNewDeal.getDeal
 * @returns {Partial<CreateNewDealFormValues>} Values for Ant Design Form setFieldsValue
 */
export function mapGetDealResponseToCreateFormPartial(deal: GetDealResponse): Partial<CreateNewDealFormValues> {
  const client = parseDealClientId(deal);
  const resellers = mapDealResellersToFormResellers(deal.resellers);
  const contactsGrouped = mapGetDealContactsToFormContacts(deal.contacts);
  const category = mapGetDealCategoriesToFormCategories(deal.categories);
  const tagIds: number[] = (deal.tags ?? []).map((t) => t.id);

  const mapped: Partial<CreateNewDealFormValues> = {
    client,
    suppliers: mapDealVendorsToSuppliers(deal.vendors),
    dealTitle: deal.subject ?? undefined,
    dealType: mapDealReasonToFormDealType(deal.deal_reason),
    reportTypeRequested: deal.report_type_id ?? undefined,
    tcvTierId: readDealTcvTierId(deal),
    budgetedCost: deal.budgeted_cost ?? 0,
    targetDate: toDealFormTargetDate(deal.target_purchase_date),
    contractStartDate: toDealFormTargetDate(deal.contract_start_date),
    contractEndDate: toDealFormTargetDate(deal.contract_end_date),
    showOnSmartCalendar: deal.show_on_smart_calendar ?? true,
    notice_period_days_required: deal.notice_period_days_required ?? undefined,
    dataUseRights: deal.data_use_rights,
    clientSubscriptionPlan:
      deal.client_contract_id != null && deal.client_contract_id >= 1 ? String(deal.client_contract_id) : undefined,
    situationSummary: deal.situation_summary ?? undefined,
    generalNotes: deal.notes ?? '',
    documentMode: (deal.documents?.length ?? 0) > 0 ? 'have' : deal.no_document_text ? 'none' : undefined,
    noDocumentText: deal.no_document_text ?? undefined,
    findingsRecommendationsOutcome: deal.findings_recommendations_and_outcome ?? undefined,
    savings_target: deal.savings_target ?? undefined,
    actual_savings: deal.actual_savings ?? undefined,
    initial_quote_year_1: deal.initial_quote_year_1 ?? undefined,
    initial_quote_full_contract_term: deal.initial_quote_full_contract_term ?? undefined,
    npi_savings_target_year_1: deal.npi_savings_target_year_1 ?? undefined,
    actual_savings_year_1: deal.actual_savings_year_1 ?? undefined,
    currency_code: (deal.currency_code ?? '').trim().toUpperCase() || 'USD',
    contacts: contactsGrouped,
    tagIds
  };

  if (resellers !== undefined) {
    mapped.reseller = resellers;
  }

  if (category !== undefined) {
    mapped.category = category;
  }

  // Custom field values are dynamic keys (by custom_field.key) — attach via a loosely-typed bag so the form
  // can rehydrate each rendered Form.Item via setFieldsValue.
  Object.assign(mapped as unknown as Record<string, unknown>, mapDealCustomFieldsToFormValues(deal.custom_fields));

  return mapped;
}

/**
 * Appends `documents_metadata` (JSON) and each `docs` file part to an existing `FormData`.
 * Same shape for POST/PATCH /deals, POST /deals/:id/documents/upload, and proposal document upload.
 * @param {FormData} formData - Multipart body to mutate
 * @param {ConfirmedDocument[]} documents - Confirmed rows in upload order; only rows with a `Blob` `file` are sent as `docs`
 * @returns {void}
 */
export function appendConfirmedDocumentsToFormData(formData: FormData, documents: ConfirmedDocument[]): void {
  const meta = documents.map((d, index) => ({
    document_category_id: d.type,
    sort_order: index + 1,
    client_has_access: d.clientVisible ?? false
  }));
  if (meta.length > 0) {
    formData.append('documents_metadata', JSON.stringify(meta));
  }
  for (const doc of documents) {
    const file = doc.file;
    if (file instanceof Blob) {
      formData.append('docs', file, file instanceof File ? file.name : doc.name);
    }
  }
}

/** Contact payload shape sent in POST /deals `contacts` JSON. */
export type CreateDealContactPayload = {
  user_id: string;
  contact_type: string;
};

/**
 * Initial deal status on create: In Progress when a Primary Analyst is assigned, otherwise Pending Assignment.
 * @param {CreateDealContactPayload[]} contacts - Contacts JSON for POST /deals.
 * @returns {'In_Progress' | 'Pending_Assignment'} Initial deal status.
 */
export function resolveCreateDealStatus(
  contacts: readonly CreateDealContactPayload[]
): 'In_Progress' | 'Pending_Assignment' {
  const hasPrimaryAnalyst = contacts.some((c) => c.contact_type === 'Primary_Analyst' && Boolean(c.user_id?.trim()));
  return hasPrimaryAnalyst ? 'In_Progress' : 'Pending_Assignment';
}

/**
 * Appends a deal money amount to a multipart body, normalising what the form actually produces.
 *
 * Antd's `<Input type="number" />` yields a string (and `''` once the user clears it), so this accepts both
 * shapes. `undefined`/`null` means "untouched / never loaded" and is skipped so a PATCH leaves the stored value
 * alone; `''` is an explicit clear and is sent as an empty value, which the API DTO normalises to 0. Values that
 * are not finite and non-negative are skipped — the form's `validateNonNegativeAmount` rule blocks them, so this
 * fails closed rather than posting garbage.
 * @param {FormData} formData - Multipart body being built.
 * @param {string} key - API field name (e.g. `initial_quote_year_1`).
 * @param {number | string | undefined} value - Raw form value for the field.
 * @returns {void}
 * @example
 * appendDealAmount(fd, 'initial_quote_year_1', 500000); // fd.get(...) === '500000'
 * appendDealAmount(fd, 'initial_quote_year_1', '');     // fd.get(...) === ''  → API stores 0
 */
function appendDealAmount(formData: FormData, key: string, value: number | string | undefined): void {
  if (value == null) return;
  const raw = String(value).trim();
  if (raw === '') {
    formData.append(key, '');
    return;
  }
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) return;
  formData.append(key, String(parsed));
}

/**
 * Maps Create New Deal form values + confirmed uploads to multipart FormData for POST /deals.
 * @param {CreateNewDealFormValues} values - Ant Design form values.
 * @param {ConfirmedDocument[]} documents - Confirmed files in upload order (matches docs[] and documents_metadata).
 * @param {Tag[]} [tags] - Tag options including in-form created entries.
 * @param {CustomField[]} [customFields] - Enabled custom field definitions for collecting `custom_fields` values.
 * @returns {FormData} Multipart body for Nest CreateDealRequestDto.
 */
export function buildNewDealCreateFormData(
  values: CreateNewDealFormValues,
  documents: ConfirmedDocument[],
  tags: Tag[] = [],
  customFields: CustomField[] = []
): FormData {
  const formData = new FormData();

  const appendOptionalString = (key: string, v: string | undefined) => {
    if (v !== undefined && v !== null && v !== '') formData.append(key, v);
  };

  const appendPositiveInt = (key: string, v: number | undefined) => {
    if (v !== undefined && v !== null && Number.isFinite(v) && v >= 1) {
      formData.append(key, String(Math.trunc(v)));
    }
  };

  const clientId = values.client !== undefined && values.client !== null ? Number(values.client) : undefined;
  appendPositiveInt('client_id', clientId);

  appendOptionalString('subject', values.dealTitle?.trim());

  const dealReason = values.dealType
    ? DEAL_TYPE_OPTIONS.find((option) => {
        return option.value === values.dealType;
      })?.value
    : undefined;
  appendOptionalString('deal_reason', dealReason);

  appendPositiveInt('report_type_id', values.reportTypeRequested);
  appendPositiveInt('tcvTierId', values.tcvTierId);

  const targetRaw = values.targetDate;
  const targetDateStr = targetRaw ? formatApiDateOnly(targetRaw) : undefined;
  appendOptionalString('target_purchase_date', targetDateStr);

  if (values.contractStartDate) {
    formData.append('contract_start_date', formatApiDateOnly(values.contractStartDate));
  }
  if (values.contractEndDate) {
    formData.append('contract_end_date', formatApiDateOnly(values.contractEndDate));
  }

  // The checkbox is on the create form too, so an untick at creation lands as
  // hidden. Absent key → the column default (visible).
  if (values.showOnSmartCalendar !== undefined) {
    formData.append('show_on_smart_calendar', String(values.showOnSmartCalendar));
  }

  appendOptionalString('data_use_rights', values.dataUseRights);

  const planId = values.clientSubscriptionPlan ? parseInt(String(values.clientSubscriptionPlan), 10) : undefined;
  appendPositiveInt('client_contract_id', planId);

  appendOptionalString('situation_summary', values.situationSummary?.trim());
  appendOptionalString('notes', values.generalNotes?.trim());
  if (values.documentMode === 'none') {
    appendOptionalString('no_document_text', values.noDocumentText?.trim());
  } else if (values.documentMode === 'have') {
    formData.append('no_document_text', '');
  }
  appendOptionalString('findings_recommendations_and_outcome', values.findingsRecommendationsOutcome?.trim());
  // Initial Quote (Year 1 + Full Term) is Vantage-owned and editable on the deal form, so it is sent here.
  // NPI Savings Target and Actual Savings stay Salesforce-managed (auto-calculated in SF and synced into
  // Vantage) and are deliberately not sent.
  appendDealAmount(formData, 'initial_quote_year_1', values.initial_quote_year_1);
  appendDealAmount(formData, 'initial_quote_full_contract_term', values.initial_quote_full_contract_term);

  const budget = values.budgetedCost;
  if (budget != null && Number.isFinite(Number(budget))) {
    formData.append('budgeted_cost', String(budget));
  }

  const noticePeriod = values.notice_period_days_required;
  if (noticePeriod != null && Number.isFinite(Number(noticePeriod))) {
    formData.append('notice_period_days_required', String(noticePeriod));
  }

  const currency = values.currency_code?.trim().toUpperCase();
  if (currency) {
    formData.append('currency_code', currency);
  }

  const parties = {
    suppliers: values.suppliers?.map((s) => {
      // An unregistered supplier has no vendor id — its `id` is a client-side-only key, so
      // `Number(id)` would send NaN. Send the typed name as `vendor_name` instead; the API creates
      // the deal_party row with a null vendor_id ("provisioning pending"). Exactly one of the two
      // identities is sent, matching the API's XOR rule.
      if (s.isUnregistered) {
        const row: { vendor_name: string; is_primary: boolean } = {
          vendor_name: s.name.trim(),
          is_primary: s.isPrimary
        };
        return row;
      }
      const row: { vendor_id: number; is_primary: boolean } = {
        vendor_id: Number(s.id),
        is_primary: s.isPrimary
      };
      return row;
    }),
    resellers: values.reseller?.map((r) => {
      // An unregistered reseller has no `reseller.id` — its client-side id is a local key, so send
      // the typed name instead. Sending `Number(localKey)` would be NaN and fail validation.
      if (r.isUnregistered) {
        const row: { reseller_name: string; is_lead_reseller: boolean } = {
          reseller_name: r.name.trim(),
          is_lead_reseller: r.isPrimary
        };
        return row;
      }
      const row: { reseller_id: number; is_lead_reseller: boolean } = {
        reseller_id: Number(r.id),
        is_lead_reseller: r.isPrimary
      };
      return row;
    })
  };
  if (parties.suppliers && parties.suppliers.length > 0) {
    formData.append('vendors', JSON.stringify(parties.suppliers));
  }
  if (parties.resellers && parties.resellers.length > 0) {
    formData.append('resellers', JSON.stringify(parties.resellers));
  }
  const contacts = [...values.contacts.npi, ...values.contacts.client];
  const contactsToSend: CreateDealContactPayload[] = contacts.map((c: DbContactFromDb) => ({
    user_id: c.id,
    contact_type: c.designatedType ?? ''
  }));
  formData.append('status', resolveCreateDealStatus(contactsToSend));
  if (contacts.length > 0) {
    formData.append('contacts', JSON.stringify(contactsToSend));
  }

  const categoryRows = flattenFormCategoriesForApi(values.category);
  if (categoryRows.length > 0) {
    formData.append('categories', JSON.stringify(categoryRows));
  }

  appendDealTagsToFormData(formData, values.tagIds, tags);

  const customFieldRows = collectDealCustomFieldsForApi(values, customFields);
  if (customFieldRows.length > 0) {
    formData.append('custom_fields', JSON.stringify(customFieldRows));
  }

  appendConfirmedDocumentsToFormData(formData, documents);

  return formData;
}

/**
 * Maps Create New Deal form values to multipart FormData for PATCH /deals/:id.
 * Omits immutable party identifiers (`client_id`, `deal_number`, `salesforce_id`, `vendors`).
 * Includes `resellers` when the form has reseller rows (same shape as POST /deals).
 * Only **new** uploads (entries with a `file` blob) are sent as `docs` / `documents_metadata`; existing deal documents are unchanged.
 * @param {CreateNewDealFormValues} values - Ant Design form values.
 * @param {ConfirmedDocument[]} newDocumentsOnly - Confirmed rows that include a file (not hydrated server-only rows).
 * @param {Tag[]} [tags] - Tag options including in-form created entries.
 * @param {CustomField[]} [customFields] - Enabled custom field definitions for collecting `custom_fields` values.
 * @param {DealUpdateRelationBaseline} [relationBaseline] - GET baseline used to diff categories/tags so scalar-only edits do not delete/reinsert relations.
 * @returns {FormData} Multipart body for Nest UpdateDealRequestDto.
 */
export function buildNewDealUpdateFormData(
  values: CreateNewDealFormValues,
  newDocumentsOnly: ConfirmedDocument[],
  tags: Tag[] = [],
  customFields: CustomField[] = [],
  relationBaseline?: DealUpdateRelationBaseline
): FormData {
  const formData = new FormData();

  const appendOptionalString = (key: string, v: string | undefined) => {
    if (v !== undefined && v !== null && v !== '') formData.append(key, v);
  };

  const appendPositiveInt = (key: string, v: number | undefined) => {
    if (v !== undefined && v !== null && Number.isFinite(v) && v >= 1) {
      formData.append(key, String(Math.trunc(v)));
    }
  };

  appendOptionalString('subject', values.dealTitle?.trim());

  const dealReason = values.dealType
    ? DEAL_TYPE_OPTIONS.find((option) => {
        return option.value === values.dealType;
      })?.value
    : undefined;
  appendOptionalString('deal_reason', dealReason);

  appendPositiveInt('report_type_id', values.reportTypeRequested);
  appendPositiveInt('tcvTierId', values.tcvTierId);

  const targetRaw = values.targetDate;
  const targetDateStr = targetRaw ? formatApiDateOnly(targetRaw) : undefined;
  appendOptionalString('target_purchase_date', targetDateStr);

  formData.append('contract_start_date', values.contractStartDate ? formatApiDateOnly(values.contractStartDate) : '');
  formData.append('contract_end_date', values.contractEndDate ? formatApiDateOnly(values.contractEndDate) : '');

  // Sent as its own key, independent of the two date keys above, so hiding a deal never touches
  // its contract dates. Only sent when the form holds a value (edit mode hydrates it from GET /deals/:id);
  // an absent key leaves the stored flag unchanged, matching how the API treats every undefined field.
  if (values.showOnSmartCalendar !== undefined) {
    formData.append('show_on_smart_calendar', String(values.showOnSmartCalendar));
  }

  appendOptionalString('data_use_rights', values.dataUseRights);

  const planId = values.clientSubscriptionPlan ? parseInt(String(values.clientSubscriptionPlan), 10) : undefined;
  appendPositiveInt('client_contract_id', planId);

  appendOptionalString('situation_summary', values.situationSummary?.trim());
  // General Notes is clearable, so it is always sent — including as ''. The API applies `notes` only
  // when the key is present (`if (dto.notes !== undefined)`), so omitting an emptied value left the
  // stored note in place and the cleared text reappeared on reopening the deal metadata (VE-2176).
  // Mirrors the contract dates and `no_document_text` below, which also append '' to clear.
  formData.append('notes', values.generalNotes.trim());
  if (values.documentMode === 'none') {
    appendOptionalString('no_document_text', values.noDocumentText?.trim());
  } else if (values.documentMode === 'have') {
    formData.append('no_document_text', '');
  }
  // Findings is clearable, so an emptied value must be sent rather than dropped — the API applies the
  // column only when the key is present, so omitting '' left the stored text in place and it
  // reappeared on reopening the deal metadata (VE-2749), the same defect fixed for `notes` above.
  // Unlike `generalNotes`, this field is optional and hydrates as `... ?? undefined`, so `undefined`
  // means "never set" (leave the stored NULL alone) while '' means "the user cleared it".
  const findingsForUpdate = values.findingsRecommendationsOutcome?.trim();
  if (findingsForUpdate !== undefined) {
    formData.append('findings_recommendations_and_outcome', findingsForUpdate);
  }
  // Initial Quote (Year 1 + Full Term) is Vantage-owned and editable on the deal form, so it is sent here.
  // NPI Savings Target and Actual Savings stay Salesforce-managed (auto-calculated in SF and synced into
  // Vantage) and are deliberately not sent.
  appendDealAmount(formData, 'initial_quote_year_1', values.initial_quote_year_1);
  appendDealAmount(formData, 'initial_quote_full_contract_term', values.initial_quote_full_contract_term);

  const budget = values.budgetedCost;
  formData.append('budgeted_cost', String(budget));

  const noticePeriod = values.notice_period_days_required;
  if (noticePeriod != null && Number.isFinite(Number(noticePeriod))) {
    formData.append('notice_period_days_required', String(noticePeriod));
  }

  const currency = values.currency_code?.trim().toUpperCase();
  if (currency) {
    formData.append('currency_code', currency);
  }

  // A row hydrated as pending that now carries a numeric id was resolved in this edit → link it.
  // The API rejects `reseller_name` on update, so only registered picks can appear here.
  const resellerLinks = (values.reseller ?? [])
    .filter((r) => r.wasPending && r.partyId && !r.isUnregistered && Number.isFinite(Number(r.id)))
    .map((r) => ({ party_id: r.partyId as string, reseller_id: Number(r.id) }));
  if (resellerLinks.length > 0) {
    formData.append('reseller_links', JSON.stringify(resellerLinks));
  }

  // `resellers` is a FULL REPLACE on update, and it is deliberately mutually exclusive with
  // `reseller_links`:
  //   - sending both would have the replace delete and recreate the very row the link just updated,
  //     changing `deal_party.id` — which is the thing linking exists to preserve.
  // So a save that resolves a pending reseller omits the replace; ordinary reseller edits go out on a
  // later save.
  //
  // An UNRESOLVED pending reseller no longer suppresses the replace: the API accepts `reseller_name`
  // on update (resellers only), preserves an existing pending row rather than recreating it, and
  // raises the provisioning request for rows the request actually adds. Its client-side id would be
  // NaN, so a pending row must be sent by NAME and never as `reseller_id`.
  const resellersPayload =
    resellerLinks.length > 0
      ? undefined
      : values.reseller?.map((r) =>
          r.isUnregistered
            ? { reseller_name: r.name.trim(), is_lead_reseller: r.isPrimary }
            : { reseller_id: Number(r.id), is_lead_reseller: r.isPrimary }
        );
  if (resellersPayload !== undefined) {
    formData.append('resellers', JSON.stringify(resellersPayload));
  }

  // A row hydrated as pending (`wasPending`) that now carries a numeric id is a party the analyst
  // resolved in this edit → link it. Rows that were already registered are excluded by `wasPending`,
  // so a scalar-only edit never re-sends a link the API would reject.
  const vendorLinks = (values.suppliers ?? [])
    .filter((s) => s.wasPending && s.partyId && !s.isUnregistered && Number.isFinite(Number(s.id)))
    .map((s) => ({ party_id: s.partyId as string, vendor_id: Number(s.id) }));
  if (vendorLinks.length > 0) {
    formData.append('vendor_links', JSON.stringify(vendorLinks));
  }

  // `vendors` is present-means-replace on update (VE-2570), like `resellers` and `categories`: what
  // is sent IS the deal's complete supplier set afterwards. So send the whole list, not just the
  // additions — and only when it actually changed, since an unchanged list would churn `deal_party`
  // rows and re-fire the Salesforce primary-vendor sync for nothing.
  const supplierRows = values.suppliers ?? [];
  const registeredSupplierRows = supplierRows
    .filter((s) => !s.isUnregistered && Number.isFinite(Number(s.id)))
    .map((s) => ({ vendor_id: Number(s.id), is_primary: s.isPrimary }));
  // A row still awaiting provisioning cannot be represented in `vendors` (the API rejects
  // `vendor_name` on update), so sending the list while one is present would read as "remove it" and
  // the reconcile would drop that party row. Hold the whole replace back until it is resolved; the
  // analyst resolves it through `vendor_links` above, or removes it, and saves again.
  const hasUnresolvedSupplier = supplierRows.some((s) => s.isUnregistered);
  // A row with no `partyId` was never hydrated from the deal, so it is definitionally new — that
  // alone proves a change without needing the baseline. Removals and primary-flag changes leave every
  // remaining row carrying its `partyId`, which is what the baseline comparison is for.
  const hasAddedSupplier = supplierRows.some((s) => !s.partyId);
  // Fingerprinted with the pending rows included, so REMOVING one registers as a change. Comparing
  // only the registered rows made "delete the pending supplier" look identical to "no edit".
  const supplierFingerprintRows = supplierRows.map((s) => ({
    vendor_id: s.isUnregistered || !Number.isFinite(Number(s.id)) ? null : Number(s.id),
    is_primary: s.isPrimary
  }));
  const supplierSetChanged =
    hasAddedSupplier ||
    (relationBaseline?.suppliers !== undefined &&
      supplierSetKey(supplierFingerprintRows) !== supplierSetKey(relationBaseline.suppliers));
  // Mutually exclusive with `vendor_links`, exactly as `resellers` is with `reseller_links`: a save
  // that resolves a pending supplier sends the link ALONE. Sending the replace too would (a) be
  // refused outright on a deal whose proposals carry BOM line items, since a replace is what the BOM
  // locks, and (b) re-state a set the link has just changed. Ordinary supplier edits go out on the
  // next save — which is why the modal says to resolve and save first.
  if (vendorLinks.length === 0 && registeredSupplierRows.length > 0 && !hasUnresolvedSupplier && supplierSetChanged) {
    formData.append('vendors', JSON.stringify(registeredSupplierRows));
  }

  const contacts = [...values.contacts.npi, ...values.contacts.client];
  const contactsToSend = contacts.map((c: DbContactFromDb) => ({ user_id: c.id, contact_type: c.designatedType }));
  if (contacts.length > 0) {
    formData.append('contacts', JSON.stringify(contactsToSend));
  }

  // Relationship fields use present-means-replace semantics in the API. Compare them against the loaded GET
  // baseline so scalar-only edits do not delete/reinsert categories or tags, while intentional clears still send [].
  const categoryRows = flattenFormCategoriesForApi(values.category);
  const baselineCategoryRows =
    relationBaseline?.categories !== undefined
      ? flattenGetDealCategoriesForApi(relationBaseline.categories)
      : undefined;
  if (
    values.category !== undefined &&
    (baselineCategoryRows === undefined || !areCategoryRowsEqual(categoryRows, baselineCategoryRows))
  ) {
    formData.append('categories', JSON.stringify(mergePartialCategoryRowsForPatch(categoryRows, baselineCategoryRows)));
  }

  const normalizedTagIds = normalizeFormTagIdsForApi(values.tagIds);
  const baselineTagIds =
    relationBaseline?.tagIds !== undefined ? normalizeFormTagIdsForApi(relationBaseline.tagIds) : undefined;
  if (
    values.tagIds !== undefined &&
    (baselineTagIds === undefined ||
      hasNewTagSentinel(values.tagIds) ||
      !areNumberArraysEqual(normalizedTagIds, baselineTagIds))
  ) {
    appendDealTagsToFormData(formData, mergePartialTagIdsForPatch(values.tagIds, baselineTagIds), tags, {
      sendEmptyTagIds: true
    });
  }

  // Present-means-replace (like categories): when the client has custom fields rendered, always send the current
  // set — even when empty — so clearing a field removes its `custom_field_value` row on the server.
  if (customFields.length > 0) {
    formData.append('custom_fields', JSON.stringify(collectDealCustomFieldsForApi(values, customFields)));
  }

  appendConfirmedDocumentsToFormData(formData, newDocumentsOnly);

  return formData;
}

/**
 * Multipart body for POST /deals/:dealId/documents/upload: `docs` + optional `documents_metadata`
 * (same shape as the document phase of POST /deals or PATCH /deals/:id).
 * @param {ConfirmedDocument[]} documents - Confirmed files in upload order.
 * @returns {FormData} Multipart body for {@link useNewDeal} uploadDealDocuments (via {@link appendConfirmedDocumentsToFormData}).
 */
export function buildDealDocumentsUploadFormData(documents: ConfirmedDocument[]): FormData {
  const formData = new FormData();
  appendConfirmedDocumentsToFormData(formData, documents);
  return formData;
}

/**
 * One item from POST /deals/:dealId/documents/upload (Nest `DealDocLinkItemDto`).
 */
export interface UploadDealDocumentsResponseItem {
  attributes: { type: string; url: string };
  linkId: string;
  ContentDocumentId: string;
  IsDeleted: boolean;
  ContentDocument: {
    attributes: { type: string; url: string };
    Id: string;
    Title: string;
    LatestPublishedVersionId: string;
    CreatedDate: string;
    ContentSize: number;
    Description?: string | null;
    FileType: string;
    FileExtension: string;
    IsFmv: boolean;
    LatestPublishedVersion: {
      attributes: { type: string; url: string };
      Id: string;
      Release_to_Client__c: boolean;
      Is_FMV_Report__c: boolean;
    };
  };
}

/**
 * One item from POST /deals/:dealId/proposals/:proposalId/documents/upload (Nest `ProposalDocumentLinkCreatedDto`).
 * The timestamp + uploader fields are populated by the API so the UI can render the new row
 * with the real `Date Added` / `Uploaded By` values immediately, without waiting for the
 * proposal-refetch that follows the upload.
 */
export interface UploadProposalDocumentsResponseItem {
  linkId: string;
  documentId: string;
  file_name: string;
  link_created_at?: string;
  uploaded_at?: string;
  uploaded_by_display_name?: string | null;
}

/** Response body from DELETE /deals/:dealId/documents/:linkId or DELETE .../proposals/:id/documents/:linkId. */
export interface DeleteDocumentLinkResponse {
  message: string;
}

/**
 * Cross-link-type pre-flight for the destroy-everywhere delete warning
 * (`GET /deals/:dealId/documents/:documentId/linkage`, Nest `DocumentLinkageDto`).
 * Reports, for ONE document within the deal, whether it is also a deal document,
 * which live proposals confirm it, and which deliverables it backs — so the
 * delete-confirm UI can NAME what a destroy will also remove. Sourced from the
 * generated OpenAPI schema (never hand-authored).
 */
export type DocumentLinkageResponse = components['schemas']['DocumentLinkageDto'];

/**
 * Response body from POST /deals/:dealId/documents/access-request (Nest `DealDocumentsAccessRequestByDealResponseDto`).
 * `success` is true when NPI was notified on this call; `isAlreadyRequested` is true once a request exists;
 * `lockedCount` is how many documents the client cannot access.
 */
export interface RequestDealDocumentsAccessResponse {
  success: boolean;
  lockedCount: number;
  isAlreadyRequested: boolean;
}

/**
 * Response body from GET /deals/:dealId/documents/access-request (Nest `DealDocumentsAccessRequestStatusDto`).
 * Lets the client deal view render "Request Access" vs "Request sent" on load.
 */
export interface DealDocumentsAccessRequestStatus {
  isAlreadyRequested: boolean;
  lockedCount: number;
}

/**
 * Hook for New Deal API calls.
 * Use this in the NewDeal page / Create New Deal form for create, fetch, update, document upload, document delete,
 * and client deal listing (GET /api/deals).
 * @returns {object} New deal API functions.
 */
export const useNewDeal = () => {
  const { axios } = useAxios(BASE_URL_V2);
  // Entitlement lookups only enrich the Report Type picker and fall back to the full catalog when they
  // fail, so they must not raise the shared error toast mid-form — see `useEntitlementsAxios`.
  const { axios: entitlementsAxios } = useEntitlementsAxios(BASE_URL_V2);

  /**
   * Normalizes supplier deals API rows into the chart/table shape.
   * @param {SupplierDeal[] | undefined} rows - Raw API rows.
   * @returns {DeliveryDeal[]} Delivery deals with derived quarter/year fields.
   */
  const normalizeSupplierDeals = (rows: SupplierDeal[] | undefined): DeliveryDeal[] => {
    const toNumber = (value: unknown): number => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const out: DeliveryDeal[] = [];
    for (const row of rows ?? []) {
      const baseDate = row.Start_Date__c ?? row.Contract_Start_Date__c ?? row.CreatedDate;
      const m = moment(baseDate);
      const year = m.isValid() ? m.format('YYYY') : moment().format('YYYY');
      const quarter = m.isValid() ? `${year}-Q${m.quarter()}` : `${year}-Q1`;

      out.push({
        Id: row.Id,
        Name: row.Name,
        CreatedDate: String(row.CreatedDate),
        Feedback_Date__c: row.Feedback_Date__c ? new Date(row.Feedback_Date__c) : null,
        Start_Date__c: row.Start_Date__c ? new Date(row.Start_Date__c) : new Date(row.CreatedDate),
        Subject__c: row.Subject__c ?? '',
        Account_Manager__r: row.Account_Manager__r
          ? {
              Id: row.Account_Manager__r.Id ?? '',
              Name: row.Account_Manager__r.Name ?? ''
            }
          : null,
        NPI_Analyst__r: {
          Id: row.NPI_Analyst__r?.Id ?? '',
          Name: row.NPI_Analyst__r?.Name ?? ''
        },
        Client__r: {
          Id: row.Client__r?.Id ?? '',
          Name: row.Client__r?.Name ?? ''
        },
        Savings_Achieved__c: (row.Savings_Achieved__c ?? 'Unknown outcome') as DeliveryDeal['Savings_Achieved__c'],
        Savings_Achieved_Year_1__c: toNumber(row.Savings_Achieved_Year_1__c),
        Savings_Achieved_Full_Contract_Term__c: toNumber(row.Savings_Achieved_Full_Contract_Term__c),
        Term__c: (row.Term__c ?? 'N/A') as DeliveryDeal['Term__c'],
        Deal_Status__c: (row.Deal_Status__c ?? 'Working') as DeliveryDeal['Deal_Status__c'],
        Status__c: (row.Status__c ?? 'In Progress') as DeliveryDeal['Status__c'],
        Contract_Type__c: (row.Contract_Type__c ?? null) as DeliveryDeal['Contract_Type__c'],
        Contract_Start_Date__c: row.Contract_Start_Date__c ? new Date(row.Contract_Start_Date__c) : null,
        Contract_Renewal_Date__c: row.Contract_Renewal_Date__c ? new Date(row.Contract_Renewal_Date__c) : null,
        Total_Savings_1yr__c: toNumber(row.Total_Savings_1yr__c),
        Total_Savings_3yr__c: toNumber(row.Total_Savings_3yr__c),
        Deal_Reason__c: (row.Deal_Reason__c ?? 'Renewal') as DeliveryDeal['Deal_Reason__c'],
        Initial_Quote_Full_Contract_Term__c: toNumber(row.Initial_Quote_Full_Contract_Term__c),
        NPI_Savings_Target__c: toNumber(row.NPI_Savings_Target__c),
        NPI_Savings_Target_Full_Contract_Term__c: toNumber(row.NPI_Savings_Target_Full_Contract_Term__c),
        Final_Amount_Full_Contract_Term__c: toNumber(row.Final_Amount_Full_Contract_Term__c),
        FMV_Report_Link__c: row.FMV_Report_Link__c ?? null,
        Was_Advisor_Network_SME_Used__c: null,
        Number_of_Partner_SME_Advisors_used__c: 0,
        quarter,
        year,
        contract_id: row.contract_id
      });
    }
    return out;
  };

  /**
   * Fetches supplier delivery deals by vendor id using Nest supplier endpoint.
   * @param {number} vendorId - Internal vendor id.
   * @returns {Promise<DeliveryDeal[] | undefined>} Delivery deals array or undefined on error.
   */
  const getSupplierDealsByVendorId = async (vendorId: number): Promise<DeliveryDeal[] | undefined> => {
    try {
      const res = await axios.get<SupplierDealsResponse>(`/supplier/${vendorId}/deals`);
      return normalizeSupplierDeals(res.data?.data);
    } catch (e) {
      console.error('API Error in useNewDeal.getSupplierDealsByVendorId:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Fetches a deal by UUID (GET /deals/:id — Nest `DealController.getById`).
   * @param {string} dealId - Deal UUID (same as create response `dealId`).
   * @returns {Promise<GetDealResponse | undefined>} Full deal payload or undefined on error.
   */
  const getDeal = async (dealId: string): Promise<GetDealResponse | undefined> => {
    try {
      //return the sample deal data instead of calling the api
      //return SAMPLE_DEAL;
      const id = dealId.trim();
      if (!id) return undefined;
      const res = await axios.get<GetDealResponse>(`/deals/${encodeURIComponent(id)}`);
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getDeal:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Creates a new deal (multipart FormData: POST /deals).
   * @param {FormData} formData - Multipart body from buildNewDealCreateFormData.
   * @param {number} [profileId] - Client variant: switched-profile client id sent as a query param so the
   *   backend derives client_id (ProfileGuard-validated). Omit for internal/Smart Bench callers.
   * @returns {Promise<CreateDealResponse | undefined>} Created deal metadata or undefined on error.
   */
  const createDeal = async (formData: FormData, profileId?: number): Promise<CreateDealResponse | undefined> => {
    try {
      const res = await axios.post<CreateDealResponse>(
        '/deals',
        formData,
        profileId != null ? { params: { profileId } } : undefined
      );
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.createDeal:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Updates an existing deal (multipart FormData: PATCH /deals/:id).
   * @param {string} dealId - Deal UUID (same as create response `dealId`).
   * @param {FormData} formData - Multipart body from buildNewDealUpdateFormData.
   * @returns {Promise<CreateDealResponse | undefined>} Updated deal metadata or undefined on error.
   */
  const updateDeal = async (dealId: string, formData: FormData): Promise<CreateDealResponse | undefined> => {
    try {
      const id = dealId.trim();
      if (!id) return undefined;
      const res = await axios.patch<CreateDealResponse>(`/deals/${encodeURIComponent(id)}`, formData);
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.updateDeal:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Deletes a deal by UUID (DELETE /deals/:id — Nest `DealController.remove`).
   * @param {string} dealId - Deal UUID.
   * @returns {Promise<DeleteDealResponse | undefined>} Delete confirmation payload or undefined on error.
   */
  const deleteDeal = async (dealId: string): Promise<DeleteDealResponse | undefined> => {
    try {
      const id = dealId.trim();
      if (!id) return undefined;
      const res = await axios.delete<DeleteDealResponse>(`/deals/${encodeURIComponent(id)}`);
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.deleteDeal:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Searches clients by name (client table; only clients with salesforce_id).
   * @param {string} str - Search string to match against client name.
   * @returns {Promise<ClientSearchResult[] | undefined>} Promise resolving to list of clients (id and name) or undefined on error.
   */
  const searchClients = async (str: string): Promise<ClientSearchResult[] | undefined> => {
    try {
      const res = await axios.get<ClientSearchResult[]>('/clients/search', { params: { str } });
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.searchClients:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Lists active contracts for an internal client id (GET /clients/{clientId}/contracts).
   * Returns an empty array when the client exists but has no matching contracts.
   * Resolves to `undefined` on error; **404** means no `client` row for this id.
   * @param {number} clientId - Internal `client.id`.
   * @returns {Promise<ClientActiveContract[] | undefined>} Contracts (newest start date first) or undefined on error.
   */
  const getClientContracts = async (clientId: number): Promise<ClientActiveContract[] | undefined> => {
    try {
      const res = await axios.get<ClientActiveContract[]>(`/clients/${clientId}/contracts`);
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getClientContracts:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Records the entitled service (report type) a benchmark request draws against — a
   * `deal_service_request` (status `Requested`). Best-effort; the caller should not block on failure.
   * @param {string} dealId - Deal UUID (from the create response)
   * @param {string} serviceTypeCode - Chosen entitled `lov_service_type.code`
   * @returns {Promise<unknown | undefined>} Created request, or undefined on error
   */
  const createDealServiceRequest = async (dealId: string, serviceTypeCode: string): Promise<unknown | undefined> => {
    try {
      const res = await axios.post(`/deals/${dealId}/service-requests`, { serviceTypeCode });
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.createDealServiceRequest:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Cancels the deal's pending benchmark service request (DELETE /deals/:id/service-requests) — used
   * when a deal is edited to a non-entitled report type so the old request doesn't linger as in-progress.
   * @param {string} dealId - Deal UUID
   * @returns {Promise<unknown | undefined>} Result, or undefined on error
   */
  const cancelDealServiceRequest = async (dealId: string): Promise<unknown | undefined> => {
    try {
      const res = await axios.delete(`/deals/${dealId}/service-requests`);
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.cancelDealServiceRequest:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Entitled services (report types) for a client contract, with usage rollups — drives the
   * benchmark-request "Report Type Requested" picker.
   * @param {number} clientId - Internal `client.id`
   * @param {number} contractId - Internal `client_contract.id`
   * @returns {Promise<ContractEntitlement[] | undefined>} Entitled services, or undefined on error
   */
  const getContractEntitlements = async (
    clientId: number,
    contractId: number
  ): Promise<ContractEntitlement[] | undefined> => {
    try {
      const res = await entitlementsAxios.get<ContractEntitlement[]>(
        `/clients/${clientId}/contracts/${contractId}/entitlements`
      );
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getContractEntitlements:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Portfolio-aware entitlements for the TCV tier picker (GET /clients/{clientId}/portfolio-entitlements).
   * Resolves the account's portfolio root and returns that top-level contract's entitlements plus whether
   * the account is part of a portfolio at all.
   * @param {number} clientId - Internal `client.id` of the requesting account.
   * @returns {Promise<PortfolioEntitlements | undefined>} Portfolio flag + root entitlements, or undefined on error.
   */
  const getPortfolioEntitlements = async (clientId: number): Promise<PortfolioEntitlements | undefined> => {
    try {
      const res = await entitlementsAxios.get<PortfolioEntitlements>(`/clients/${clientId}/portfolio-entitlements`);
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getPortfolioEntitlements:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Active Total Contract Value tiers (GET /deals/tcv-tiers). Pass `includeId` (a deal's persisted
   * `tcv_tier_id`) on the edit screen so a since-deactivated tier is still returned — otherwise the saved
   * selection would vanish from the picker and block the edit.
   * @param {number} [includeId] - Persisted tier id to include even if inactive.
   * @returns {Promise<TcvTier[] | undefined>} Active tiers (plus the included one), or undefined on error.
   */
  const getTcvTiers = async (includeId?: number): Promise<TcvTier[] | undefined> => {
    try {
      const res = await axios.get<TcvTier[]>('/deals/tcv-tiers', {
        params: includeId != null ? { includeId } : undefined
      });
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getTcvTiers:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Lists Salesforce-backed deals for the authenticated client (GET /api/deals — Nest `DealClientController.getClientDeals`).
   * @param {GetClientDealsQuery} [query] - Optional `includeAll` and `lastModifiedDate` filters.
   * @returns {Promise<ClientDealListingRecord[] | undefined>} Deal rows or undefined on error.
   */
  const getClientDeals = async (query?: GetClientDealsQuery): Promise<ClientDealListingRecord[] | undefined> => {
    try {
      const res = await axios.get<ClientDealListingRecord[]>('/api/deals', { params: query });
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getClientDeals:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Elasticsearch-backed grouped deal/workspace search (GET /deals/search).
   * Query must be at least 2 characters (API validation).
   * @param {string} q - Search string.
   * @param {number} limit - Max results per entity bucket (1–20; API default 10).
   * @param {DealSearchEntityType[] | undefined} [types] - When non-empty, restricts which entity buckets are queried; sent as comma-separated `types` (not array encoding) so the URL matches `types=deal`; omitted or empty uses API default (all four).
   * @returns {Promise<DealSearchResponse | undefined>} Grouped hits or undefined on error.
   */
  const searchDeals = async (
    q: string,
    limit = 15,
    types?: DealSearchEntityType[]
  ): Promise<DealSearchResponse | undefined> => {
    const trimmed = q.trim();
    if (trimmed.length < 2) return undefined;
    try {
      const params: { q: string; limit: number; types?: string } = {
        q: trimmed,
        limit
      };
      if (types?.length) {
        params.types = types.join(',');
      }
      const res = await axios.get<DealSearchResponse>('/deals/search', { params });
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.searchDeals:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Gets open deals list for SmartBench workspace (GET /deals/open).
   * @param {GetOpenDealsQuery | undefined} query - Pagination/filter query.
   * @returns {Promise<OpenDealsResponse | undefined>} Open deals list with totals and averages, or undefined on error.
   */
  const getOpenDeals = async (query?: GetOpenDealsQuery): Promise<OpenDealsResponse | undefined> => {
    const requestKey = `GET /deals/open ${JSON.stringify(query ?? {})}`;
    return dedupeGetRequest(requestKey, async () => {
      try {
        const res = await axios.get<OpenDealsResponse>('/deals/open', { params: query });
        return res.data;
      } catch (e) {
        console.error('API Error in useNewDeal.getOpenDeals:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    });
  };

  /**
   * Lists submitted deals for the All Submitted Deals page (GET /deals/submitted).
   * @param {number} [profileId] - Acting profile id (client / portfolio company).
   * @param {number[]} [subscriptionPlanIds] - Optional client_contract ids; when present, only deals on those plans are returned.
   * @param {boolean} [includeAll] - When true, includes deals outside the default scope.
   * @param {DealScopeFilterMode} [filterMode] - Which scope filter to apply; defaults to subscriptionPlan (VE-2153).
   * @param {string | null} [contractStartFrom] - Inclusive lower bound on deal.contract_start_date (YYYY-MM-DD); sent only in date mode.
   * @param {string | null} [contractStartTo] - Inclusive upper bound on deal.contract_start_date (YYYY-MM-DD); sent only in date mode.
   * @returns {Promise<SubmittedDealRoot[] | undefined>} Deals payload or undefined on error.
   */
  const getSubmittedDeals = async (
    profileId?: number,
    subscriptionPlanIds?: number[],
    includeAll?: boolean,
    filterMode?: DealScopeFilterMode,
    contractStartFrom?: string | null,
    contractStartTo?: string | null
  ): Promise<SubmittedDealRoot[] | undefined> => {
    try {
      const dateMode = filterMode === 'contractStartDate';
      const res = await axios.get<SubmittedDealRoot[]>('/deals/submitted', {
        params: {
          profileId,
          // Plan ids are meaningless in date mode — omit so the server can't
          // read a stale selection as an explicit plan scope.
          subscriptionPlanIds:
            !dateMode && subscriptionPlanIds && subscriptionPlanIds.length > 0
              ? subscriptionPlanIds.join(',')
              : undefined,
          includeAll,
          filterMode,
          contractStartFrom: dateMode ? (contractStartFrom ?? undefined) : undefined,
          contractStartTo: dateMode ? (contractStartTo ?? undefined) : undefined
        }
      });
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getSubmittedDeals:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Lists subscription plans (client_contract rows) for the submitted-deals filter dropdown.
   * @param {number} [profileId] - Acting profile id.
   * @returns {Promise<SubscriptionPlan[] | undefined>} Plans across portfolio.
   */
  const getSubscriptionPlans = async (profileId?: number): Promise<SubscriptionPlan[] | undefined> => {
    try {
      const res = await axios.get<SubscriptionPlan[]>('/deals/subscription-plans', {
        params: { profileId }
      });
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getSubscriptionPlans:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Lists enabled custom-field definitions for the resolved client. Drives the
   * profile-specific columns on the Submitted Deals page.
   * @param {number} [profileId] - Acting profile id.
   * @returns {Promise<CustomFieldDef[] | undefined>} Definitions or undefined on error.
   */
  const getCustomFields = async (profileId?: number): Promise<CustomFieldDef[] | undefined> => {
    try {
      const res = await axios.get<CustomFieldDef[]>('/deals/custom-fields', {
        params: { profileId }
      });
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getCustomFields:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Lists portfolio companies for the All Submitted Deals page (GET /deals/portfolio).
   * @param {number} [profileId] - Acting profile id.
   * @returns {Promise<PortfolioCompany[] | undefined>} Portfolio companies or undefined on error.
   */
  const getSubmittedPortfolioCompanies = async (profileId?: number): Promise<PortfolioCompany[] | undefined> => {
    try {
      const res = await axios.get<PortfolioCompany[]>('/deals/portfolio', {
        params: { profileId }
      });
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getSubmittedPortfolioCompanies:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Lists company users for analyst assignment (GET /deals/company-users).
   * @returns {Promise<CompanyUser[] | undefined>} Company users or undefined on error.
   */
  const getDealCompanyUsers = async (): Promise<CompanyUser[] | undefined> => {
    if (companyUsersSessionCache !== undefined) {
      return companyUsersSessionCache;
    }
    return dedupeGetRequest('GET /deals/company-users', async () => {
      try {
        const res = await axios.get<CompanyUser[]>('/deals/company-users');
        companyUsersSessionCache = res.data;
        return res.data;
      } catch (e) {
        console.error('API Error in useNewDeal.getDealCompanyUsers:', e);
        await AxiosErrorHandler(e);
        return undefined;
      }
    });
  };

  /**
   * Assigns or clears the primary analyst for a deal (PATCH /deals/:id/analyst).
   * @param {string} dealId - Deal UUID.
   * @param {string | null | undefined} analystUserId - Analyst user UUID, null/undefined to clear.
   * @returns {Promise<UpdateDealAnalystResponse | undefined>} Updated analyst response or undefined on error.
   */
  const updateDealAnalyst = async (
    dealId: string,
    analystUserId?: string | null
  ): Promise<UpdateDealAnalystResponse | undefined> => {
    try {
      const id = dealId.trim();
      if (!id) return undefined;
      const res = await axios.patch<UpdateDealAnalystResponse>(`/deals/${encodeURIComponent(id)}/analyst`, {
        analystUserId: analystUserId ?? null
      });
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.updateDealAnalyst:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Updates customer-facing comments for a deal (PATCH /deals/:id/comments-to-customer).
   * @param {string} dealId - Deal UUID.
   * @param {string | null | undefined} commentsToCustomer - Comment text, null/undefined to clear.
   * @returns {Promise<UpdateDealCommentsResponse | undefined>} Updated comments response or undefined on error.
   */
  const updateDealCommentsToCustomer = async (
    dealId: string,
    commentsToCustomer?: string | null
  ): Promise<UpdateDealCommentsResponse | undefined> => {
    try {
      const id = dealId.trim();
      if (!id) return undefined;
      const res = await axios.patch<UpdateDealCommentsResponse>(
        `/deals/${encodeURIComponent(id)}/comments-to-customer`,
        { commentsToCustomer: commentsToCustomer ?? null }
      );
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.updateDealCommentsToCustomer:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Gets scoped deals for an internal client id (GET /deals/scope/client/:clientId).
   * Same item shape as `GET /deals/open` `items[]` (no list-level averages).
   * @param {number} clientId - Internal client id.
   * @returns {Promise<OpenDealItem[] | undefined>} Deals or undefined on error.
   */
  const getDealsByClientScope = async (clientId: number): Promise<OpenDealItem[] | undefined> => {
    try {
      const res = await axios.get<OpenDealItem[]>(`/deals/scope/client/${clientId}`);
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getDealsByClientScope:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Gets scoped deals for an internal vendor id (GET /deals/scope/vendor/:vendorId).
   * Same item shape as `GET /deals/open` `items[]` (no list-level averages).
   * @param {number} vendorId - Internal vendor id.
   * @returns {Promise<OpenDealItem[] | undefined>} Deals or undefined on error.
   */
  const getDealsByVendorScope = async (vendorId: number): Promise<OpenDealItem[] | undefined> => {
    try {
      const res = await axios.get<OpenDealItem[]>(`/deals/scope/vendor/${vendorId}`);
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getDealsByVendorScope:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Gets scoped deals for an internal reseller id (GET /deals/scope/reseller/:resellerId).
   * Same item shape as `GET /deals/open` `items[]` (no list-level averages).
   * @param {number} resellerId - Internal reseller id.
   * @returns {Promise<OpenDealItem[] | undefined>} Deals or undefined on error.
   */
  const getDealsByResellerScope = async (resellerId: number): Promise<OpenDealItem[] | undefined> => {
    try {
      const res = await axios.get<OpenDealItem[]>(`/deals/scope/reseller/${resellerId}`);
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getDealsByResellerScope:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Fetches LOV document categories.
   * @param {boolean} [clientCanSet] - When true, request only client-settable categories (`?clientCanSet=true`).
   * @returns {Promise<LovDocumentCategory[] | undefined>} Promise resolving to document categories or undefined on error.
   */
  const getDocumentCategories = async (clientCanSet?: boolean): Promise<LovDocumentCategory[] | undefined> => {
    try {
      const res = await axios.get<LovDocumentCategory[]>('/lov/document-category', {
        params: clientCanSet ? { clientCanSet: true } : undefined
      });
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getDocumentCategories:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Fetches LOV report types.
   * @returns {Promise<LovReportType[] | undefined>} Promise resolving to report types or undefined on error.
   */
  const getReportTypes = async (): Promise<LovReportType[] | undefined> => {
    try {
      const res = await axios.get<LovReportType[]>('/lov/report-type');
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getReportTypes:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Fetches active currencies (GET /lov/currency).
   * @returns {Promise<LovCurrency[] | undefined>} Currency codes or undefined on error.
   */
  const getCurrencies = async (): Promise<LovCurrency[] | undefined> => {
    try {
      const res = await axios.get<Array<{ currency_code?: string; currencyCode?: string }>>('/lov/currency');
      const rows = res.data ?? [];
      const out: LovCurrency[] = [];
      for (const row of rows) {
        const code = String(row.currency_code ?? row.currencyCode ?? '').trim();
        if (code) out.push({ currency_code: code.toUpperCase() });
      }
      return out.length > 0 ? out : undefined;
    } catch (e) {
      console.error('API Error in useNewDeal.getCurrencies:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Searches or lists contacts from the database only (GET /contacts/from-db → ContactController.searchContactsFromDb).
   * Supports `name` + `contactType` filters; optional `groupBy=type` for grouped response.
   * @param {GetContactsFromDbParams | undefined} params - profileId, name, contactType, groupBy, etc.
   * @returns {Promise<DbContactFromDb[] | DbContactsGroupedFromDb | undefined>} Flat list, grouped object, or undefined on error.
   */
  const getContactsFromDb = async (
    params?: GetContactsFromDbParams
  ): Promise<DbContactFromDb[] | DbContactsGroupedFromDb | undefined> => {
    try {
      const nameTrimmed = params?.name?.trim();
      const res = await axios.get<DbContactFromDb[] | DbContactsGroupedFromDb>('/contacts/from-db', {
        params: {
          ...(params?.profileId != null ? { profileId: params.profileId } : {}),
          ...(params?.groupBy === 'type' ? { groupBy: 'type' } : {}),
          ...(nameTrimmed ? { name: nameTrimmed } : {}),
          ...(params?.contactType != null ? { contactType: params.contactType } : {}),
          ...(params?.includeUnregistered ? { includeUnregistered: 'true' } : {})
        }
      });
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getContactsFromDb:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Uploads deal or proposal documents (multipart).
   * - Deal: POST /deals/:dealId/documents/upload → {@link UploadDealDocumentsResponseItem}
   * - Proposal: POST /deals/:dealId/proposals/:proposalId/documents/upload when `proposalId` is set → {@link UploadProposalDocumentsResponseItem}
   * Use {@link buildDealDocumentsUploadFormData} to build the body from confirmed uploads.
   * @param {string} dealId - Deal UUID.
   * @param {FormData} formData - Multipart with `docs` and optional `documents_metadata` JSON.
   * @param {string} [proposalId] - When provided, uploads to the proposal document endpoint instead of deal-level.
   * @param {(percent: number) => void} [onUploadProgress] - Receives whole-percent transfer progress (0-100)
   *   so a caller can render a real progress bar. Reports bytes leaving the browser only — the server is
   *   still storing the file after this reaches 100. Never fires when the body length is unknown.
   * @returns {Promise<UploadDealDocumentsResponseItem[] | UploadProposalDocumentsResponseItem[] | undefined>} Created links or undefined on error.
   */
  const uploadDealDocuments = async (
    dealId: string,
    formData: FormData,
    proposalId?: string,
    onUploadProgress?: (percent: number) => void
  ): Promise<UploadDealDocumentsResponseItem[] | UploadProposalDocumentsResponseItem[] | undefined> => {
    try {
      const id = dealId.trim();
      if (!id) return undefined;
      // Normalised to a percent here so callers never take an axios type as a dependency.
      const config = onUploadProgress
        ? {
            onUploadProgress: (event: { loaded: number; total?: number }): void => {
              if (!event.total) return;
              onUploadProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
            }
          }
        : undefined;
      const proposalTrimmed = proposalId?.trim();
      if (proposalTrimmed) {
        const res = await axios.post<UploadProposalDocumentsResponseItem[]>(
          `/deals/${encodeURIComponent(id)}/proposals/${encodeURIComponent(proposalTrimmed)}/documents/upload`,
          formData,
          config
        );
        return res.data;
      }
      const res = await axios.post<UploadDealDocumentsResponseItem[]>(
        `/deals/${encodeURIComponent(id)}/documents/upload`,
        formData,
        config
      );
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.uploadDealDocuments:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Deletes a deal or proposal document link.
   * - Deal: DELETE /deals/:dealId/documents/:linkId — `linkId` is `deal_document_link` id (UUID).
   * - Proposal: DELETE /deals/:dealId/proposals/:proposalId/documents/:linkId when `proposalId` is set — `linkId` is `deal_proposal_document_link` id.
   * @param {string} dealId - Deal UUID.
   * @param {string} linkId - Document link UUID.
   * @param {string} [proposalId] - When provided, deletes the proposal document link; otherwise deletes the deal-level link.
   * @returns {Promise<DeleteDocumentLinkResponse | undefined>} API confirmation or undefined on error.
   */
  const deleteDealDocument = async (
    dealId: string,
    linkId: string,
    proposalId?: string
  ): Promise<DeleteDocumentLinkResponse | undefined> => {
    try {
      const d = dealId.trim();
      const l = linkId.trim();
      if (!d || !l) return undefined;
      const proposalTrimmed = proposalId?.trim();
      if (proposalTrimmed) {
        const res = await axios.delete<DeleteDocumentLinkResponse>(
          `/deals/${encodeURIComponent(d)}/proposals/${encodeURIComponent(
            proposalTrimmed
          )}/documents/${encodeURIComponent(l)}`
        );
        return res.data;
      }
      const res = await axios.delete<DeleteDocumentLinkResponse>(
        `/deals/${encodeURIComponent(d)}/documents/${encodeURIComponent(l)}`
      );
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.deleteDealDocument:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Cross-link-type pre-flight for the destroy-everywhere delete warning, keyed by the
   * `document` id (NOT a link id) → GET /deals/:dealId/documents/:documentId/linkage.
   * Reports what a destroy of this one document will also remove elsewhere on the deal.
   * @param {string} dealId - Deal UUID.
   * @param {string} documentId - `document` id (UUID) — from the row's `document_id`, not `linkId`.
   * @returns {Promise<DocumentLinkageResult<DocumentLinkageResponse>>} The linkage summary, or the
   *   REASON it could not be read. Callers MUST treat any `ok: false` as fail-closed: do not open a
   *   destructive confirm, do not delete — and report `forbidden` as a permission problem rather
   *   than inviting a retry that can never succeed.
   */
  const getDocumentLinkage = async (
    dealId: string,
    documentId: string
  ): Promise<DocumentLinkageResult<DocumentLinkageResponse>> => {
    try {
      const d = dealId.trim();
      const docId = documentId.trim();
      if (!d || !docId) return { ok: false, reason: 'unavailable' };
      // Typed `unknown`: useAxios's interceptor RESOLVES with the AxiosError on an HTTP failure
      // (see documentLinkage.ts), so this is not necessarily a response. classify* narrows it.
      const res: unknown = await axios.get<DocumentLinkageResponse>(
        `/deals/${encodeURIComponent(d)}/documents/${encodeURIComponent(docId)}/linkage`
      );
      return classifyLinkageResponse<DocumentLinkageResponse>(res);
    } catch (e) {
      console.error('API Error in useNewDeal.getDocumentLinkage:', e);
      // Route through the centralized handler for Bugsnag telemetry WITHOUT rethrowing: awaiting it
      // rejects, which would throw out of this catch and leave the fail-closed caller with an
      // unhandled rejection instead of a message. Mirrors createContactUser in useContact.ts.
      void AxiosErrorHandler(e).catch(() => undefined);
      return { ok: false, reason: 'unavailable' };
    }
  };

  /**
   * Requests access to a deal's restricted documents (POST /deals/:dealId/documents/access-request).
   * Client deal-view action: the API detects documents the client cannot access
   * (`deal_document_link.client_has_access = false`) and notifies NPI by email.
   * @param {string} dealId - Deal UUID.
   * @returns {Promise<RequestDealDocumentsAccessResponse | undefined>} Notification result or undefined on error.
   */
  const requestDealDocumentsAccess = async (
    dealId: string
  ): Promise<RequestDealDocumentsAccessResponse | undefined> => {
    try {
      const id = dealId.trim();
      if (!id) return undefined;
      const res = await axios.post<RequestDealDocumentsAccessResponse>(
        `/deals/${encodeURIComponent(id)}/documents/access-request`
      );
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.requestDealDocumentsAccess:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Fetches the access-request status for a deal's restricted documents
   * (GET /deals/:dealId/documents/access-request).
   * @param {string} dealId - Deal UUID.
   * @returns {Promise<DealDocumentsAccessRequestStatus | undefined>} Status or undefined on error.
   */
  const getDealDocumentsAccessRequestStatus = async (
    dealId: string
  ): Promise<DealDocumentsAccessRequestStatus | undefined> => {
    try {
      const id = dealId.trim();
      if (!id) return undefined;
      const res = await axios.get<DealDocumentsAccessRequestStatus>(
        `/deals/${encodeURIComponent(id)}/documents/access-request`
      );
      return res.data;
    } catch (e) {
      console.error('API Error in useNewDeal.getDealDocumentsAccessRequestStatus:', e);
      await AxiosErrorHandler(e);
      return undefined;
    }
  };

  /**
   * Grants or removes client access to a deal document (analyst/internal action).
   * PATCH /deals/:dealId/documents/:linkId/client-access — flips `deal_document_link.client_has_access`.
   * @param {string} dealId - Deal UUID.
   * @param {string} linkId - deal_document_link id (UUID).
   * @param {boolean} clientHasAccess - True to release to the client, false to re-restrict.
   * @returns {Promise<boolean>} True when the update succeeded.
   */
  const setDealDocumentClientAccess = async (
    dealId: string,
    linkId: string,
    clientHasAccess: boolean
  ): Promise<boolean> => {
    try {
      const d = dealId.trim();
      const l = linkId.trim();
      if (!d || !l) return false;
      await axios.patch(`/deals/${encodeURIComponent(d)}/documents/${encodeURIComponent(l)}/client-access`, {
        client_has_access: clientHasAccess
      });
      return true;
    } catch (e) {
      console.error('API Error in useNewDeal.setDealDocumentClientAccess:', e);
      await AxiosErrorHandler(e);
      return false;
    }
  };

  /**
   * Requests access to a single locked document on behalf of a client
   * (POST /deals/:dealId/documents/:linkId/access-request).
   * @param {string} dealId - Deal UUID
   * @param {string} linkId - `deal_document_link` id (UUID)
   * @returns {Promise<boolean>} True when the request was sent or already existed
   */
  const requestDealDocumentAccess = async (dealId: string, linkId: string): Promise<boolean> => {
    try {
      const d = dealId.trim();
      const l = linkId.trim();
      if (!d || !l) return false;
      await axios.post(`/deals/${encodeURIComponent(d)}/documents/${encodeURIComponent(l)}/access-request`);
      return true;
    } catch (e) {
      console.error('API Error in useNewDeal.requestDealDocumentAccess:', e);
      await AxiosErrorHandler(e);
      return false;
    }
  };

  return {
    getSupplierDealsByVendorId,
    getDeal,
    createDeal,
    updateDeal,
    deleteDeal,
    uploadDealDocuments,
    deleteDealDocument,
    getDocumentLinkage,
    requestDealDocumentsAccess,
    getDealDocumentsAccessRequestStatus,
    setDealDocumentClientAccess,
    requestDealDocumentAccess,
    searchClients,
    getClientContracts,
    getContractEntitlements,
    getPortfolioEntitlements,
    getTcvTiers,
    createDealServiceRequest,
    cancelDealServiceRequest,
    getClientDeals,
    getOpenDeals,
    getSubmittedDeals,
    getSubmittedPortfolioCompanies,
    getSubscriptionPlans,
    getCustomFields,
    searchDeals,
    getDealCompanyUsers,
    updateDealAnalyst,
    updateDealCommentsToCustomer,
    getDealsByClientScope,
    getDealsByVendorScope,
    getDealsByResellerScope,
    getDocumentCategories,
    getReportTypes,
    getCurrencies,
    getContactsFromDb
  };
};
